import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import { alpha } from "@mui/material/styles";
import { formatAmount } from "../../../utils/hooks/dataHandler";
import {
  getTaxAdjustmentOptions,
  SECTION_OPTIONS
} from "./ColumnSelectionReviewStep";
import ClassificationSummaryDialog from "./ClassificationSummaryDialog";
import ApplySnapshotDialog from "./ApplySnapshotDialog";
import { headerStyle } from "../../MainTaxComputation/utils/DetailsTable";

const PARTIAL_AMOUNT_PATTERN = /^-?\d*\.?\d*$/;
const IGNORE_SECTION = "Ignore";
const IGNORE_LABEL = "Original Subtotals / Ignored";
const IGNORE_TOOLTIP =
  "These rows are not counted as available accounts, so source totals such as net sales or gross profit are not double-counted. Change the Section field to Revenue, Other Income, Cost of Sales, or Expenses to include a row.";

const REVIEW_COLUMN_SX = {
  control: { maxWidth: 48, minWidth: 48, width: 48 },
  name: { minWidth: 220, width: 220 },
  accountCode: { minWidth: 120, width: 120 },
  amount: { minWidth: 130, width: 130 },
  taxAdjustment: { minWidth: 180, width: 180 },
  section: { minWidth: 150, width: 150 },
  actions: { maxWidth: 112, minWidth: 112, width: 112 }
};

const createRowId = () =>
  (typeof window !== "undefined" && window.crypto?.randomUUID?.()) ||
  `row-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const formatFinancialAmount = (amount) => {
  const numericValue = Number(amount);
  if (!Number.isFinite(numericValue)) {
    return { text: "—", negative: false, value: 0 };
  }
  return {
    text: formatAmount(numericValue, false, false, false, true),
    negative: numericValue < 0,
    value: numericValue
  };
};

const getCurrencyLabel = (currency) => {
  if (currency === "MYR") return "RM";
  if (currency === "SGD") return "S$";
  return currency || "";
};

const FinancialAmount = ({
  amount,
  currency,
  fontWeight,
  variant = "body2"
}) => {
  const { text, negative } = formatFinancialAmount(amount);
  const currencyLabel = getCurrencyLabel(currency);
  return (
    <Typography
      variant={variant}
      sx={{
        color: negative ? "error.main" : "text.primary",
        fontWeight: fontWeight || (negative ? 600 : 500)
      }}
    >
      {currencyLabel ? `${currencyLabel} ${text}` : text}
    </Typography>
  );
};

const displaySectionLabel = (section) =>
  section === IGNORE_SECTION ? IGNORE_LABEL : section;

const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === "";

const getRowFailures = (row) => {
  const failures = [];
  if (isBlank(row.name)) failures.push("name");
  if (row.amount === "" || row.amount === null || row.amount === undefined) {
    failures.push("amount");
  } else if (!Number.isFinite(Number(row.amount))) {
    failures.push("amount");
  }
  if (isBlank(row.section)) failures.push("section");
  return failures;
};

const toChildRow = (item) => ({
  row_id: item.row_id || createRowId(),
  accountCode: item.accountCode ?? "",
  name: item.name ?? "",
  amount: item.amount ?? "",
  taxAdjustment: item.taxAdjustment ?? ""
});

const normalizeClassifiedRow = (row) => {
  const history = Array.isArray(row?.history) ? row.history : [];
  return {
    row_id: row?.row_id || createRowId(),
    accountCode: row?.accountCode ?? "",
    name: row?.name ?? row?.accountName ?? "",
    amount: row?.amount ?? "",
    taxAdjustment: row?.taxAdjustment ?? "",
    section: row?.section ?? "",
    reference: row?.reference ?? "",
    history,
    unmerged: Boolean(row?.unmerged),
    childRows: Array.isArray(row?.childRows)
      ? row.childRows.map(toChildRow)
      : []
  };
};

const unwrapClassifyResponse = (result) => {
  const inner = result?.data?.data
    ? result.data
    : Array.isArray(result?.data)
      ? { data: result.data, options: result.options }
      : result?.data || result;
  const rows = Array.isArray(inner?.data)
    ? inner.data
    : Array.isArray(inner)
      ? inner
      : [];
  return {
    rows: rows.map(normalizeClassifiedRow),
    options: inner?.options || {}
  };
};

const sectionAmount = (rows, section) =>
  rows
    .filter((row) => row.section === section)
    .reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

const buildProfitStatement = (rows) => {
  const revenue = sectionAmount(rows, "Revenue");
  const otherIncome = sectionAmount(rows, "Other Income");
  const costOfSales = sectionAmount(rows, "Cost of Sales");
  const expenses = sectionAmount(rows, "Expenses");
  const grossProfit = revenue - costOfSales;
  const netProfit = revenue + otherIncome - costOfSales - expenses;
  return {
    revenue,
    otherIncome,
    costOfSales,
    expenses,
    grossProfit,
    netProfit
  };
};

const sectionOrder = (section) => {
  if (section === "Needs attention") return -1;
  const index = SECTION_OPTIONS.indexOf(section);
  return index === -1 ? SECTION_OPTIONS.length : index;
};

const AmountField = ({ value, error, onChange }) => {
  const [focused, setFocused] = useState(false);
  const numericValue = Number(String(value ?? "").replace(/,/g, ""));
  const displayValue = focused
    ? String(value ?? "").replace(/,/g, "")
    : Number.isFinite(numericValue) && String(value).trim() !== ""
      ? formatFinancialAmount(numericValue).text
      : (value ?? "");

  return (
    <TextField
      variant='standard'
      size='small'
      fullWidth
      value={displayValue}
      error={error}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(event) => {
        const nextValue = event.target.value.replace(/,/g, "");
        if (!PARTIAL_AMOUNT_PATTERN.test(nextValue)) return;
        onChange(nextValue);
      }}
      inputProps={{
        style: { textAlign: "right" },
        inputMode: "decimal"
      }}
      sx={{
        "& .MuiInput-input": {
          color:
            Number.isFinite(numericValue) && numericValue < 0
              ? "error.main"
              : "inherit",
          fontWeight:
            Number.isFinite(numericValue) && numericValue < 0 ? 600 : 400
        }
      }}
    />
  );
};

const ReviewSyncStep = ({
  classifiedRows,
  originalRows = [],
  companyType,
  currency,
  classifyOptions,
  onRowsChange,
  onBack,
  onApply,
  existingSectionCounts = {},
  existingTaggedCount = 0,
  applying = false
}) => {
  const theme = useTheme();
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const taxAdjustmentOptions = useMemo(
    () => getTaxAdjustmentOptions(companyType),
    [companyType]
  );

  const rows = useMemo(() => classifiedRows || [], [classifiedRows]);
  const profit = useMemo(() => buildProfitStatement(rows), [rows]);
  const failures = useMemo(
    () =>
      rows
        .map((row, index) => ({
          row,
          index,
          missing: getRowFailures(row)
        }))
        .filter((item) => item.missing.length > 0),
    [rows]
  );

  const groupedRows = useMemo(() => {
    const groups = new Map();
    rows.forEach((row) => {
      const invalid = getRowFailures(row).length > 0;
      const key = invalid ? "Needs attention" : row.section || "Unassigned";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });
    return [...groups.entries()].sort(
      ([left], [right]) => sectionOrder(left) - sectionOrder(right)
    );
  }, [rows]);
  const tableHeaderCellSx = {
    ...headerStyle(theme),
    bgcolor: "white",
    verticalAlign: "middle"
  };
  const tableBodyCellSx = {
    borderColor: alpha(theme.palette.divider, 0.1),
    overflow: "hidden",
    px: 1.25,
    py: 0.75,
    textOverflow: "ellipsis",
    verticalAlign: "middle"
  };
  const tableControlCellSx = {
    ...tableBodyCellSx,
    height: 44,
    p: 0,
    textAlign: "center"
  };
  const tableHeaderControlCellSx = {
    ...tableHeaderCellSx,
    p: 0,
    textAlign: "center"
  };

  const updateRow = (rowId, field, value) => {
    onRowsChange(
      rows.map((row) =>
        row.row_id === rowId ? { ...row, [field]: value } : row
      )
    );
  };

  const updateChild = (parentId, childId, field, value) => {
    onRowsChange(
      rows.map((row) => {
        if (row.row_id !== parentId) return row;
        const childRows = (row.childRows || []).map((child) =>
          child.row_id === childId ? { ...child, [field]: value } : child
        );
        const amount = childRows.reduce(
          (sum, child) => sum + (Number(child.amount) || 0),
          0
        );
        return { ...row, childRows, amount };
      })
    );
  };

  const unmergeRow = (rowId) => {
    onRowsChange(
      rows.map((row) => {
        if (row.row_id !== rowId || (row.history || []).length <= 1) return row;
        const childRows =
          row.childRows?.length > 0
            ? row.childRows
            : row.history.map(toChildRow);
        return { ...row, unmerged: true, childRows };
      })
    );
    setExpandedIds((previous) => new Set(previous).add(rowId));
  };

  const restoreMerge = (rowId) => {
    onRowsChange(
      rows.map((row) =>
        row.row_id === rowId ? { ...row, unmerged: false } : row
      )
    );
  };

  const toggleExpanded = (rowId) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden",
        p: 0.5,
        width: "100%"
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden", width: "100%" }}
      >
        <Paper
          variant='outlined'
          sx={{
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            maxHeight: { xs: "none", md: "100%" },
            minHeight: 0,
            overflow: "auto",
            p: { xs: 1.25, sm: 1.5 },
            width: { xs: "100%", md: 300, lg: 320 }
          }}
        >
          <Box sx={{ mb: 1.25 }}>
            <Typography variant='h6' fontWeight={700} lineHeight={1.2}>
              Classified Result
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              Review the classified accounts before applying them to Detailed
              P&amp;L.
            </Typography>
          </Box>

          <Button
            fullWidth
            variant='outlined'
            onClick={() => setSummaryOpen(true)}
            sx={{
              textTransform: "none",
              color: "info.main",
              borderColor: "info.main",
              "&:hover": {
                borderColor: "info.main",
                bgcolor: alpha(theme.palette.info.main, 0.04)
              }
            }}
            color='info'
            disabled={applying}
            startIcon={
              <InfoOutlinedIcon sx={{ fontSize: 18, color: "info.main" }} />
            }
          >
            Check what has changed
          </Button>

          {failures.length > 0 && (
            <Alert
              severity='error'
              sx={{
                mt: 1.25,
                maxHeight: { xs: 96, sm: 128, md: 180 },
                overflow: "auto"
              }}
            >
              {failures.length} row{failures.length === 1 ? "" : "s"} need
              attention before apply. Missing name, amount, or section:
              <Box component='ul' sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                {failures.map(({ row, index, missing }) => (
                  <li key={row.row_id}>
                    {row.name || `Row ${index + 1}`}: missing{" "}
                    {missing.join(", ")}
                  </li>
                ))}
              </Box>
            </Alert>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Box>
            <Typography
              variant='caption'
              color='text.secondary'
              fontWeight={700}
            >
              Calculated net profit
            </Typography>
            <Stack alignItems='flex-start' sx={{ mt: 0.5 }}>
              <FinancialAmount
                amount={profit.netProfit}
                currency={currency}
                variant='h6'
                fontWeight={800}
              />
            </Stack>
          </Box>

          <Box sx={{ flexGrow: 1, minHeight: 12 }} />
          <Box
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              pt: 1.25
            }}
          >
            <Stack direction='row' spacing={1}>
              {onBack && (
                <Button
                  variant='outlined'
                  onClick={onBack}
                  sx={{ textTransform: "none", flexShrink: 0 }}
                  disabled={applying}
                >
                  Back
                </Button>
              )}
              <Tooltip
                title={
                  onApply
                    ? "Review a snapshot, then replace the current P&L or add these accounts to it."
                    : "Apply is unavailable because this wizard is not connected to the detailed P&L."
                }
              >
                <Box component='span' sx={{ display: "block", flex: 1 }}>
                  <Button
                    fullWidth
                    variant='contained'
                    sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                    disabled={!onApply || applying}
                    onClick={() => setApplyOpen(true)}
                  >
                    Apply to Detailed P&amp;L
                  </Button>
                </Box>
              </Tooltip>
            </Stack>
          </Box>
        </Paper>

        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            minHeight: { xs: 240, md: 0 },
            minWidth: 0,
            overflow: "hidden",
            width: "100%"
          }}
        >
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{
              display: { xs: "block", lg: "none" },
              flexShrink: 0,
              mb: 0.75,
              px: 0.25
            }}
          >
            Swipe or scroll horizontally to see all columns.
          </Typography>
          <TableContainer
            component={Paper}
            variant='outlined'
            sx={{
              borderRadius: 0,
              boxShadow: "none",
              flex: 1,
              maxWidth: "100%",
              minHeight: 0,
              overflowX: "auto",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              "& .MuiTable-root": {
                minWidth: 960,
                width: "max-content"
              }
            }}
          >
          <Table stickyHeader size='small'>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...tableHeaderControlCellSx,
                    ...REVIEW_COLUMN_SX.control,
                    borderRight: "1px solid",
                    borderRightColor: "divider",
                    left: 0,
                    position: "sticky",
                    zIndex: 4
                  }}
                />
                <TableCell
                  sx={{
                    ...tableHeaderCellSx,
                    ...REVIEW_COLUMN_SX.name,
                    borderRight: "1px solid",
                    borderRightColor: "divider",
                    left: 48,
                    position: "sticky",
                    zIndex: 4
                  }}
                >
                  Account name
                </TableCell>
                <TableCell
                  sx={{
                    ...tableHeaderCellSx,
                    ...REVIEW_COLUMN_SX.accountCode
                  }}
                >
                  Account code
                </TableCell>
                <TableCell
                  align='right'
                  sx={{ ...tableHeaderCellSx, ...REVIEW_COLUMN_SX.amount }}
                >
                  Amount
                </TableCell>
                <TableCell
                  sx={{
                    ...tableHeaderCellSx,
                    ...REVIEW_COLUMN_SX.taxAdjustment
                  }}
                >
                  Tax adjustment
                </TableCell>
                <TableCell
                  sx={{ ...tableHeaderCellSx, ...REVIEW_COLUMN_SX.section }}
                >
                  Section
                </TableCell>
                <TableCell
                  align='center'
                  sx={{
                    ...tableHeaderControlCellSx,
                    ...REVIEW_COLUMN_SX.actions
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedRows.map(([section, sectionRows]) => {
                const sectionTotal = sectionRows.reduce(
                  (sum, row) => sum + (Number(row.amount) || 0),
                  0
                );
                const isIgnore = section === IGNORE_SECTION;
                return (
                  <React.Fragment key={section}>
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        sx={{
                          ...tableBodyCellSx,
                          bgcolor: (theme) =>
                            alpha(
                              theme.palette.primary.main,
                              isIgnore ? 0.04 : 0.14
                            ),
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          px: 1.25,
                          py: 0.75
                        }}
                      >
                        <Stack
                          direction='row'
                          justifyContent='space-between'
                          alignItems='center'
                        >
                          <Stack
                            direction='row'
                            spacing={0.75}
                            alignItems='center'
                            flexWrap='wrap'
                            useFlexGap
                          >
                            <Typography
                              variant='subtitle2'
                              sx={{ fontWeight: 800 }}
                            >
                              {displaySectionLabel(section)}
                            </Typography>
                            {isIgnore && (
                              <Tooltip title={IGNORE_TOOLTIP}>
                                <InfoOutlinedIcon
                                  sx={{ fontSize: 16, color: "text.secondary" }}
                                />
                              </Tooltip>
                            )}
                            <Chip
                              size='small'
                              variant='outlined'
                              label={`${sectionRows.length} line${
                                sectionRows.length === 1 ? "" : "s"
                              }`}
                            />
                            {isIgnore && (
                              <Chip
                                size='small'
                                color='warning'
                                variant='outlined'
                                label='Not counted'
                              />
                            )}
                          </Stack>
                          <FinancialAmount
                            amount={sectionTotal}
                            fontWeight={800}
                          />
                        </Stack>
                        {isIgnore && (
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            display='block'
                            sx={{ mt: 0.5 }}
                          >
                            These are not available account rows and are
                            excluded from totals. Change the Section field on a
                            row to include it.
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    {sectionRows.map((row) => {
                      const merged = (row.history || []).length > 1;
                      const missing = getRowFailures(row);
                      const expanded =
                        expandedIds.has(row.row_id) || row.unmerged;
                      const treeItems = row.unmerged
                        ? row.childRows || []
                        : row.history || [];
                      return (
                        <React.Fragment key={row.row_id}>
                          <TableRow
                            sx={{
                              bgcolor: merged
                                ? (theme) =>
                                    alpha(theme.palette.warning.main, 0.1)
                                : missing.length
                                  ? (theme) =>
                                      alpha(theme.palette.error.main, 0.06)
                                  : "inherit",
                              "&:hover": {
                                bgcolor: merged
                                  ? (theme) =>
                                      alpha(theme.palette.warning.main, 0.14)
                                  : missing.length
                                    ? (theme) =>
                                        alpha(theme.palette.error.main, 0.08)
                                    : (theme) =>
                                        alpha(theme.palette.primary.main, 0.04)
                              }
                            }}
                          >
                            <TableCell
                              sx={{
                                ...tableControlCellSx,
                                ...REVIEW_COLUMN_SX.control,
                                bgcolor: "background.paper",
                                borderRight: "1px solid",
                                borderRightColor: "divider",
                                left: 0,
                                position: "sticky",
                                zIndex: 2
                              }}
                            >
                              {merged && (
                                <IconButton
                                  size='small'
                                  onClick={() => toggleExpanded(row.row_id)}
                                  aria-label={
                                    expanded
                                      ? "Hide source lines"
                                      : "Show source lines"
                                  }
                                  sx={{ height: 32, width: 32 }}
                                >
                                  {expanded ? (
                                    <ExpandLessIcon fontSize='small' />
                                  ) : (
                                    <ExpandMoreIcon fontSize='small' />
                                  )}
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...tableBodyCellSx,
                                ...REVIEW_COLUMN_SX.name,
                                bgcolor: "background.paper",
                                borderRight: "1px solid",
                                borderRightColor: "divider",
                                left: 48,
                                position: "sticky",
                                py: 0.5,
                                zIndex: 2
                              }}
                            >
                              <Stack spacing={0.5}>
                                <TextField
                                  variant='standard'
                                  size='small'
                                  fullWidth
                                  value={row.name}
                                  onChange={(event) =>
                                    updateRow(
                                      row.row_id,
                                      "name",
                                      event.target.value
                                    )
                                  }
                                  error={missing.includes("name")}
                                />
                                {merged && (
                                  <Chip
                                    size='small'
                                    color='warning'
                                    label={
                                      row.unmerged
                                        ? `Split in place · ${treeItems.length} lines`
                                        : `Merged · ${row.history.length} lines`
                                    }
                                    sx={{ alignSelf: "flex-start" }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell
                              sx={{
                                ...tableBodyCellSx,
                                ...REVIEW_COLUMN_SX.accountCode,
                                py: 0.5
                              }}
                            >
                              <TextField
                                variant='standard'
                                size='small'
                                fullWidth
                                value={row.accountCode ?? ""}
                                disabled={row.unmerged}
                                onChange={(event) =>
                                  updateRow(
                                    row.row_id,
                                    "accountCode",
                                    event.target.value
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell
                              align='right'
                              sx={{
                                ...tableBodyCellSx,
                                ...REVIEW_COLUMN_SX.amount,
                                py: 0.25
                              }}
                            >
                              {row.unmerged ? (
                                <Box sx={{ pr: 1 }}>
                                  <FinancialAmount
                                    amount={row.amount}
                                    fontWeight={700}
                                  />
                                </Box>
                              ) : (
                                <AmountField
                                  value={row.amount}
                                  error={missing.includes("amount")}
                                  onChange={(value) =>
                                    updateRow(row.row_id, "amount", value)
                                  }
                                />
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...tableBodyCellSx,
                                ...REVIEW_COLUMN_SX.taxAdjustment,
                                py: 0.5
                              }}
                            >
                              <Select
                                variant='standard'
                                size='small'
                                fullWidth
                                displayEmpty
                                value={row.taxAdjustment ?? ""}
                                disabled={row.unmerged}
                                onChange={(event) =>
                                  updateRow(
                                    row.row_id,
                                    "taxAdjustment",
                                    event.target.value
                                  )
                                }
                              >
                                {taxAdjustmentOptions.map((option) => (
                                  <MenuItem
                                    key={`${option.group}-${option.value || option.label}`}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>
                            <TableCell
                              sx={{
                                ...tableBodyCellSx,
                                ...REVIEW_COLUMN_SX.section,
                                py: 0.5
                              }}
                            >
                              <Select
                                variant='standard'
                                size='small'
                                fullWidth
                                displayEmpty
                                value={row.section ?? ""}
                                error={missing.includes("section")}
                                onChange={(event) =>
                                  updateRow(
                                    row.row_id,
                                    "section",
                                    event.target.value
                                  )
                                }
                              >
                                <MenuItem value=''>
                                  <em>Select</em>
                                </MenuItem>
                                {SECTION_OPTIONS.map((option) => (
                                  <MenuItem key={option} value={option}>
                                    {option === IGNORE_SECTION
                                      ? `${IGNORE_LABEL} (not counted)`
                                      : displaySectionLabel(option)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>
                            <TableCell
                              align='center'
                              sx={{
                                ...tableControlCellSx,
                                ...REVIEW_COLUMN_SX.actions
                              }}
                            >
                              {merged && !row.unmerged && (
                                <Tooltip title='Show the source lines under this account without scattering them in the list'>
                                  <Button
                                    size='small'
                                    startIcon={<CallSplitIcon />}
                                    onClick={() => unmergeRow(row.row_id)}
                                    sx={{ textTransform: "none" }}
                                  >
                                    Unmerge
                                  </Button>
                                </Tooltip>
                              )}
                              {row.unmerged && (
                                <Tooltip title='Keep these lines as one merged account'>
                                  <Button
                                    size='small'
                                    startIcon={<UndoIcon />}
                                    onClick={() => restoreMerge(row.row_id)}
                                    sx={{ textTransform: "none" }}
                                  >
                                    Restore
                                  </Button>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                          {merged && (
                            <TableRow>
                              <TableCell
                                colSpan={7}
                                sx={{
                                  borderColor: alpha(
                                    theme.palette.divider,
                                    0.1
                                  ),
                                  py: 0,
                                  borderBottom: expanded ? undefined : "none"
                                }}
                              >
                                <Collapse in={expanded} unmountOnExit>
                                  <Box
                                    sx={{
                                      bgcolor: (theme) =>
                                        alpha(
                                          theme.palette.primary.main,
                                          0.025
                                        ),
                                      ml: 6,
                                      mr: 1.25,
                                      my: 0.75,
                                      px: 1.5,
                                      py: 0.75,
                                      borderLeft: "3px solid",
                                      borderColor: "warning.main"
                                    }}
                                  >
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      fontWeight={700}
                                    >
                                      {row.unmerged
                                        ? "Source lines — edit here; parent amount follows the total"
                                        : "Source lines in this merge"}
                                    </Typography>
                                    {row.unmerged
                                      ? treeItems.map((item) => (
                                          <Stack
                                            key={item.row_id}
                                            direction={{
                                              xs: "column",
                                              sm: "row"
                                            }}
                                            spacing={1}
                                            alignItems={{ sm: "center" }}
                                            sx={{ py: 0.75 }}
                                          >
                                            <TextField
                                              variant='standard'
                                              size='small'
                                              label='Name'
                                              value={item.name}
                                              onChange={(event) =>
                                                updateChild(
                                                  row.row_id,
                                                  item.row_id,
                                                  "name",
                                                  event.target.value
                                                )
                                              }
                                              sx={{ flex: 2 }}
                                            />
                                            <TextField
                                              variant='standard'
                                              size='small'
                                              label='Account code'
                                              value={item.accountCode ?? ""}
                                              onChange={(event) =>
                                                updateChild(
                                                  row.row_id,
                                                  item.row_id,
                                                  "accountCode",
                                                  event.target.value
                                                )
                                              }
                                              sx={{ flex: 1 }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                              <AmountField
                                                value={item.amount}
                                                onChange={(value) =>
                                                  updateChild(
                                                    row.row_id,
                                                    item.row_id,
                                                    "amount",
                                                    value
                                                  )
                                                }
                                              />
                                            </Box>
                                            <Select
                                              variant='standard'
                                              size='small'
                                              displayEmpty
                                              value={item.taxAdjustment ?? ""}
                                              onChange={(event) =>
                                                updateChild(
                                                  row.row_id,
                                                  item.row_id,
                                                  "taxAdjustment",
                                                  event.target.value
                                                )
                                              }
                                              sx={{ flex: 1.4 }}
                                            >
                                              {taxAdjustmentOptions.map(
                                                (option) => (
                                                  <MenuItem
                                                    key={`${item.row_id}-${option.value || option.label}`}
                                                    value={option.value}
                                                  >
                                                    {option.label}
                                                  </MenuItem>
                                                )
                                              )}
                                            </Select>
                                          </Stack>
                                        ))
                                      : treeItems.map((item, historyIndex) => (
                                          <Stack
                                            key={`${row.row_id}-history-${historyIndex}`}
                                            direction='row'
                                            justifyContent='space-between'
                                            sx={{ py: 0.5 }}
                                          >
                                            <Typography variant='body2'>
                                              {item.name || "—"}
                                              {item.accountCode
                                                ? ` · ${item.accountCode}`
                                                : ""}
                                            </Typography>
                                            <FinancialAmount
                                              amount={item.amount}
                                            />
                                          </Stack>
                                        ))}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      </Stack>

      <ClassificationSummaryDialog
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        originalRows={originalRows}
        classifiedRows={rows}
        profit={profit}
        currency={currency}
        failures={failures}
        classifyOptions={classifyOptions}
        taxAdjustmentOptions={taxAdjustmentOptions}
      />
      <ApplySnapshotDialog
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        classifiedRows={rows}
        currency={currency}
        classifyOptions={classifyOptions}
        taxAdjustmentOptions={taxAdjustmentOptions}
        existingSectionCounts={existingSectionCounts}
        existingTaggedCount={existingTaggedCount}
        applying={applying}
        onConfirm={onApply}
      />
    </Box>
  );
};

export { unwrapClassifyResponse, normalizeClassifiedRow, getRowFailures };
export default ReviewSyncStep;
